import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../auth/useAuth";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import { createHealthRecordServiceClient, type Vaccination, type VaccinationInput } from "./client";

export type VaccinationsStatus = "idle" | "loading" | "ready" | "saving" | "error";

export function useVaccinations() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const apiBaseUrl = config?.healthRecordService?.apiBaseUrl;
  const [vaccinations, setVaccinations] = useState<Vaccination[]>([]);
  const [status, setStatus] = useState<VaccinationsStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const client = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }

    return createHealthRecordServiceClient(apiBaseUrl, () => auth.user?.access_token);
  }, [apiBaseUrl, auth.user]);

  const loadVaccinations = useCallback(
    async (signal?: AbortSignal) => {
      if (!client) {
        setError("Vaccination records are not available yet.");
        setStatus("error");
        return;
      }

      setStatus("loading");
      setError(null);
      setSaved(false);

      try {
        const records = await client.listVaccinations(signal);
        setVaccinations(records);
        setStatus("ready");
      } catch (nextError) {
        if (nextError instanceof DOMException && nextError.name === "AbortError") {
          return;
        }

        setError(nextError instanceof Error ? nextError.message : "We could not load your vaccination records.");
        setStatus("error");
      }
    },
    [client],
  );

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return undefined;
    }

    const controller = new AbortController();
    void loadVaccinations(controller.signal);

    return () => controller.abort();
  }, [auth.isAuthenticated, loadVaccinations]);

  useEffect(() => {
    if (!saved) {
      return undefined;
    }

    const timeout = window.setTimeout(() => setSaved(false), 3000);
    return () => window.clearTimeout(timeout);
  }, [saved]);

  const createVaccination = useCallback(
    async (payload: VaccinationInput) => {
      if (!client) {
        setError("Vaccination records are not available yet.");
        setStatus("error");
        return null;
      }

      setStatus("saving");
      setError(null);
      setSaved(false);

      try {
        const record = await client.createVaccination(payload);
        setVaccinations((current) => [record, ...current]);
        setStatus("ready");
        setSaved(true);
        return record;
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "We could not save the vaccination record.");
        setStatus("ready");
        return null;
      }
    },
    [client],
  );

  const updateVaccination = useCallback(
    async (vaccinationId: string, payload: Partial<VaccinationInput>) => {
      if (!client) {
        setError("Vaccination records are not available yet.");
        setStatus("error");
        return null;
      }

      setStatus("saving");
      setError(null);
      setSaved(false);

      try {
        const record = await client.updateVaccination(vaccinationId, payload);
        setVaccinations((current) => current.map((item) => (item.vaccination_id === vaccinationId ? record : item)));
        setStatus("ready");
        setSaved(true);
        return record;
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "We could not save the vaccination record.");
        setStatus("ready");
        return null;
      }
    },
    [client],
  );

  return {
    createVaccination,
    error,
    loadVaccinations,
    saved,
    status,
    updateVaccination,
    vaccinations,
  };
}
